/**
 * Hero Stats Builder
 * สร้างข้อมูล stats สำหรับ HeroIntelPanel จากข้อมูล Excel
 * 
 * ROV Draft Assistant
 */
function normalizeHeroKey(s) {
  return (s ?? '').toString().trim(); // เก็บตัวพิมพ์เดิมไว้ (เพราะ key ใน intel อาจเป็นแบบนี้)
}

function normalizePosition(pos) {
  const p = (pos ?? '').toString().trim().toUpperCase();
  if (!p) return null;

  // รองรับหลายชื่อ
  if (['JUNGLE', 'JUG', 'JGL'].includes(p)) return 'JUG';
  if (['MID', 'MAGE'].includes(p)) return 'MID';
  if (['DSL', 'TOP', 'FIGHTER'].includes(p)) return 'DSL';
  if (['ADL', 'ADC', 'CARRY', 'MARKSMAN', 'BOT'].includes(p)) return 'ADL';
  if (['SUP', 'SUPPORT', 'ROAM', 'ROAMING'].includes(p)) return 'SUP';

  // ถ้าส่ง DSL/JUG/MID/ADL/SUP มาอยู่แล้วก็ใช้เลย
  return p;
}

function resolveArgs(a, b, c, d) {
  // รูปแบบใหม่: buildHeroStats(heroOrName, { position, side, intel, opponent, lane, role })
  if (b && typeof b === 'object' && !Array.isArray(b)) {
    const heroOrName = a;
    const opt = b || {};

    const heroName =
      typeof heroOrName === 'string'
        ? heroOrName
        : (heroOrName?.name ?? '');

    // position: ให้พยายามดึงจาก opt ก่อน ถ้าไม่มีค่อยเดาจาก hero.role
    const rawPos =
      opt.position ?? opt.lane ?? opt.pos ??
      (typeof heroOrName === 'object' ? (heroOrName?.lane ?? heroOrName?.position ?? heroOrName?.role) : null);

    const position = normalizePosition(rawPos) || 'MID';
    const side = (opt.side || 'fs').toString();
    const intel = opt.intel || null;
    const opponent = opt.opponent || null;

    return {
      heroName: normalizeHeroKey(heroName),
      position,
      intel,
      side,
      opponent,
    };
  }

  // รูปแบบเดิม: buildHeroStats(heroName, position, intel, side)
  return {
    heroName: normalizeHeroKey(a),
    position: normalizePosition(b) || 'MID',
    intel: c || null,
    side: (d || 'fs').toString(),
    opponent: null,
  };
}

/**
 * Normalize value to 0-100 scale
 */
function normalizeValue(value, min, max, scale = 100) {
  if (value === null || value === undefined) return 50;
  const normalized = ((value - min) / (max - min)) * scale;
  return Math.min(scale, Math.max(0, normalized));
}

/**
 * Calculate threat level based on opponent data
 */
function calculateThreatLevel(heroName, intel) {
  if (!intel) return 50;
  
  const currentOpp = intel.currentOpponent;
  const oppData = intel.oppByTeam?.[currentOpp]?.[heroName];
  const globalData = intel.global?.[heroName];
  
  const oppWinRate = oppData?.winRate || 50;
  const oppPickCount = oppData?.pick || 0;
  const banRate = globalData?.banRate || 0;
  
  // Weighted calculation
  const threat = (oppWinRate * 0.5) + (oppPickCount * 3) + (banRate * 0.3);
  return Math.min(100, Math.max(0, threat));
}

/**
 * Generate recent form based on win rate
 */
function generateRecentForm(winRate, gameCount = 10) {
  const form = [];
  const actualGames = Math.min(gameCount, 10);
  
  for (let i = 0; i < actualGames; i++) {
    form.push(Math.random() * 100 < winRate ? 'W' : 'L');
  }
  return form;
}

/**
 * Main function: Build hero stats object
 * 
 * @param {string} heroName - ชื่อฮีโร่
 * @param {string} position - ตำแหน่ง (DSL, JUG, MID, ADL, SUP)
 * @param {object} intel - ข้อมูลจาก buildDraftIntel()
 * @param {string} side - 'fs' (ทีมเรา ตามรหัสในชีต) หรือ 'opp' (Opponent)
 * @returns {object} Stats object for HeroIntelPanel
 */
export function buildHeroStats(a, b, c, d) {
  const { heroName, position, intel, side, opponent } = resolveArgs(a, b, c, d);

  // Default values (ใช้ของเดิมคุณทั้งหมด)
  const defaultStats = {
    position: position || '',
    avgKills: 0,
    avgDeaths: 0,
    avgAssists: 0,
    avgGPM: 0,
    avgParticipation: 0,
    winRate: 50,
    pickRate: 0,
    banRate: 0,
    totalGames: 0,
    radarStats: [
      { label: 'DMG', value: 50 },
      { label: 'Survive', value: 50 },
      { label: 'Gold', value: 50 },
      { label: 'Team', value: 50 },
      { label: 'Early', value: 50 },
    ],
    threatLevel: 50,
    vsWinRate: 50,
    recentForm: ['W', 'L', 'W', 'W', 'L'],
    comparison: {
      ourPick: 0,
      theirPick: 0,
      ourWR: 50,
      theirWR: 50,
      ourGPM: 650,
      theirGPM: 620,
    },
  };

  if (!intel || !heroName) return defaultStats;

  // ✅ inject currentOpponent จาก opponent ที่ส่งมา (ถ้ามี)
  const intel2 = opponent ? { ...intel, currentOpponent: opponent } : intel;

  // --- โค้ดเดิมของคุณด้านล่าง “เหมือนเดิม” แต่เปลี่ยน intel -> intel2 ---
  const fsData = intel2.fs?.[heroName];
  const globalData = intel2.global?.[heroName];
  const currentOpp = intel2.currentOpponent;
  const oppData = intel2.oppByTeam?.[currentOpp]?.[heroName];
  const scrimData = intel2.scrimStats?.[heroName];

  const posKey = position || 'MID';
  const colPrefix = side === 'fs' ? 'FS' : 'OP';

  const totalGames = fsData?.pick || globalData?.pick || 0;
  const totalForRate = intel2.totalGames || 127;

  const winRate = fsData?.winRate ?? globalData?.winRate ?? 50;
  const pickRate = (totalGames / totalForRate) * 100;
  const banRate = ((fsData?.ban || globalData?.ban || 0) / totalForRate) * 100;

  const avgKills = scrimData?.[`Kill ${colPrefix} ${posKey}`] || 0;
  const avgDeaths = scrimData?.[`Death ${colPrefix} ${posKey}`] || 0;
  const avgAssists = scrimData?.[`Assist ${colPrefix} ${posKey}`] || 0;
  const avgGPM = scrimData?.[`GPM ${colPrefix} ${posKey}`] || 0;
  const participation = scrimData?.[`participation rate ${colPrefix} ${posKey}`] || 0;

  const damagePerMin = scrimData?.[`Damage/Min ${colPrefix} ${posKey}`] || 0;
  const deathAt2Min = scrimData?.[`2' ${colPrefix} ${posKey}`] || 0;

  const radarStats = [
    { label: 'DMG', value: normalizeValue(damagePerMin, 0, 1500) },
    { label: 'Survive', value: 100 - normalizeValue(avgDeaths, 0, 5) },
    { label: 'Gold', value: normalizeValue(avgGPM, 0, 900) },
    { label: 'Team', value: normalizeValue(participation, 0, 1) },
    { label: 'Early', value: 100 - normalizeValue(deathAt2Min, 0, 3) },
  ];

  // threatLevel ต้องใช้ intel2 ด้วย
  const threatLevel = calculateThreatLevel(heroName, intel2);

  const vsWinRate = intel2.vsWinRate?.[heroName] ?? (100 - (oppData?.winRate || 50));
  const recentForm = generateRecentForm(winRate, totalGames);

  const comparison = {
    ourPick: fsData?.pick || 0,
    theirPick: oppData?.pick || 0,
    ourWR: fsData?.winRate || 50,
    theirWR: oppData?.winRate || 50,
    ourGPM: scrimData?.[`GPM FS ${posKey}`] || 650,
    theirGPM: scrimData?.[`GPM OP ${posKey}`] || 620,
  };

  return {
    position: posKey,
    avgKills,
    avgDeaths,
    avgAssists,
    avgGPM,
    avgParticipation: participation * 100,
    winRate,
    pickRate,
    banRate,
    totalGames,
    radarStats,
    threatLevel,
    vsWinRate,
    recentForm,
    comparison,
  };
}


/**
 * Quick stats for tooltip (lighter version)
 */
export function buildQuickStats(heroName, intel) {
  const fsData = intel?.fs?.[heroName];
  const globalData = intel?.global?.[heroName];
  const totalGames = fsData?.pick || globalData?.pick || 0;
  const totalForRate = intel?.totalGames || 127;
  
  return {
    name: heroName,
    winRate: fsData?.winRate ?? globalData?.winRate ?? 50,
    pickRate: (totalGames / totalForRate) * 100,
    banRate: ((fsData?.ban || globalData?.ban || 0) / totalForRate) * 100,
    totalGames,
    threatLevel: calculateThreatLevel(heroName, intel),
  };
}

export default {
  buildHeroStats,
  buildQuickStats,
  normalizeValue,
  calculateThreatLevel,
};
